import React from 'react';
import { Box, useTheme } from '@mui/material';
import EChartsRenderer from '../../components/EChartsRenderer';
import { EmptyState } from '../feedback/EmptyState';
import type { SparklinePoint } from './Sparkline';

export interface TrendChartProps {
  /** One point per period, oldest first. `provisional` points are not final yet. */
  points: SparklinePoint[];
  formatValue: (value: number) => string;
  /** What the line measures, e.g. "Approval rate". */
  seriesLabel: string;
  /** px, or `100%` to fill a container with a set height, like an expanded view. */
  height?: number | string;
  /** How a provisional point is described in the readout. */
  provisionalLabel?: string;
  emptyMessage?: string;
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * One measure over time: a line with a faint area under it, a mark on every
 * period and a larger one on the latest.
 *
 * Periods that are not final yet run on as a dashed line with hollow marks.
 * That is the most important distinction on a trend — "what happened" against
 * "what has not finished happening" — so it is drawn, not left to a legend.
 * Hovering reads out the period, the value, and whether it is final.
 *
 * Colours come from the theme tokens, so it follows dark mode and the
 * tenant's brand. For a trend inside a card or table cell, use `Sparkline`.
 */
export function TrendChart({
  points,
  formatValue,
  seriesLabel,
  height = 240,
  provisionalLabel = 'Still settling',
  emptyMessage = 'No data for this range.',
}: TrendChartProps) {
  const theme = useTheme();
  const tk = theme.tokens;

  // Read through a ref so a new formatter each render does not rebuild the
  // chart; the points change whenever what they format does.
  const formatRef = React.useRef(formatValue);
  formatRef.current = formatValue;

  const option = React.useMemo(() => {
    const cut = points.findIndex((p) => p.provisional);
    const settledCount = cut < 0 ? points.length : cut;
    let lastIndex = -1;
    points.forEach((p, i) => {
      if (p.value !== null) lastIndex = i;
    });

    // ECharts' own marker for "no value here" is '-'. A null, or an item whose
    // value is null, breaks its line animation once an area fill is on.
    const GAP = '-';
    const mark = (p: SparklinePoint, i: number, hollow: boolean) =>
      p.value === null
        ? GAP
        : {
            value: p.value,
            symbolSize: i === lastIndex ? 9 : 6,
            itemStyle: hollow
              ? { color: tk.surface.raised, borderColor: tk.brand.primary, borderWidth: 1.5 }
              : { color: tk.brand.primary, borderColor: tk.surface.raised, borderWidth: i === lastIndex ? 2 : 0 },
          };

    const settled = points.map((p, i) => (i < settledCount ? mark(p, i, false) : GAP));
    // Starts one point early so the dashed run joins the solid line.
    const provisional = points.map((p, i) => (i >= settledCount - 1 ? mark(p, i, i >= settledCount) : GAP));

    return {
      // Morphing a line with gaps into a line with a different number of
      // points is where ECharts' animation crashes ("reading 'length'"), and
      // the metric select swaps the whole line at once. Draw without it.
      animation: false,
      grid: { left: 4, right: 12, top: 12, bottom: 4, containLabel: true },
      xAxis: {
        type: 'category',
        data: points.map((p) => p.label),
        boundaryGap: false,
        axisLine: { lineStyle: { color: tk.chart.axisLine } },
        axisTick: { show: false },
        axisLabel: {
          color: tk.chart.axis,
          fontSize: 12,
          hideOverlap: true,
          // The first and last points sit on the chart's edges. Centred
          // labels there run under the y-axis figures and off the right
          // side; anchoring them to the edge keeps the corner clear.
          alignMinLabel: 'left',
          alignMaxLabel: 'right',
        },
      },
      yAxis: {
        type: 'value',
        splitNumber: 4,
        axisLabel: { color: tk.chart.axis, fontSize: 12, formatter: (v: number) => formatRef.current(v) },
        splitLine: { lineStyle: { color: tk.chart.grid, type: 'dashed' } },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'line', lineStyle: { color: tk.border.strong } },
        formatter: (params: Array<{ dataIndex: number }>) => {
          const p = points[params[0]?.dataIndex ?? 0];
          if (!p) return '';
          const value = p.value === null ? '—' : formatRef.current(p.value);
          const note = p.provisional
            ? `<div style="color:${tk.semantic.warning};font-size:12px;margin-top:2px">${escapeHtml(provisionalLabel)}</div>`
            : '';
          return `<div style="font-weight:600">${escapeHtml(p.label)}</div>`
            + `<div>${escapeHtml(seriesLabel)}: <b>${escapeHtml(value)}</b></div>${note}`;
        },
      },
      series: [
        {
          name: seriesLabel,
          type: 'line',
          data: settled,
          symbol: 'circle',
          connectNulls: false,
          z: 3,
          lineStyle: { width: 2, color: tk.brand.primary },
          // A flat tint rather than a gradient: ECharts' canvas gradients
          // failed to paint here ("addColorStop … undefined"), and a faint
          // flat fill reads the same at this size.
          areaStyle: { color: tk.brand.primary, opacity: 0.08 },
        },
        {
          name: `${seriesLabel} (${provisionalLabel.toLowerCase()})`,
          type: 'line',
          data: provisional,
          symbol: 'circle',
          connectNulls: false,
          z: 2,
          lineStyle: { width: 2, color: tk.brand.primary, type: 'dashed', opacity: 0.7 },
        },
      ],
    };
  }, [points, seriesLabel, provisionalLabel, tk]);

  if (!points.some((p) => p.value !== null)) return <EmptyState message={emptyMessage} height={typeof height === 'number' ? height : 240} />;

  return (
    <Box sx={{ height, minWidth: 0 }}>
      <EChartsRenderer option={option} fillContainer />
    </Box>
  );
}
